/**
 * Graded real-repo + vendored-fixture benchmark rows — mirrored verbatim from
 * `packages/cli/src/eval/published-benchmark.json` (see `benchmark-data.ts`
 * for the sync chain). Import from `benchmark-data.ts`, not from here.
 */

export interface GradedRepoRow {
  readonly framework: string;
  /** GitHub `owner/name` slug — verbatim, links to the public repo. */
  readonly repo: string;
  /** Hand-derived ground-truth endpoint count (read from the repo's routes). */
  readonly truthCount: number;
  readonly recall: number;
  readonly precision: number;
}

export interface VendoredFixtureRow {
  readonly framework: string;
  readonly name: string;
  readonly structuralF1: number;
  readonly threshold: number;
  /** Ground-truth oracle: `openapi` (spec-derived) or `hand-labelled`. */
  readonly groundTruth: string;
}

// prettier-ignore
export const GRADED_REPO_ROWS: readonly GradedRepoRow[] = [
  { framework: 'Express', repo: 'gothinkster/node-express-realworld-example-app', truthCount: 20, recall: 1, precision: 1 },
  { framework: 'NestJS', repo: 'lujakob/nestjs-realworld-example-app', truthCount: 21, recall: 1, precision: 1 },
  { framework: 'FastAPI', repo: 'nsidnev/fastapi-realworld-example-app', truthCount: 19, recall: 1, precision: 1 },
  { framework: 'Flask', repo: 'gothinkster/flask-realworld-example-app', truthCount: 19, recall: 1, precision: 1 },
  { framework: 'Django', repo: 'gothinkster/django-realworld-example-app', truthCount: 23, recall: 1, precision: 1 },
  { framework: 'Rails', repo: 'gothinkster/rails-realworld-example-app', truthCount: 20, recall: 1, precision: 1 },
  { framework: 'Laravel', repo: 'f1amy/laravel-realworld-example-app', truthCount: 19, recall: 1, precision: 1 },
  { framework: 'Gin', repo: 'gothinkster/golang-gin-realworld-example-app', truthCount: 20, recall: 1, precision: 1 },
  { framework: 'Spring Boot', repo: 'gothinkster/spring-boot-realworld-example-app', truthCount: 19, recall: 1, precision: 1 },
  { framework: 'Actix', repo: 'snamiki1212/realworld-v1-rust-actix-web-diesel', truthCount: 20, recall: 1, precision: 1 },
  { framework: 'ASP.NET Core', repo: 'gothinkster/aspnetcore-realworld-example-app', truthCount: 19, recall: 1, precision: 1 },
  { framework: 'GraphQL', repo: 'howtographql/graphql-js', truthCount: 8, recall: 1, precision: 1 },
  { framework: 'Fastify', repo: 'fastify/demo', truthCount: 14, recall: 1, precision: 1 },
  { framework: 'gRPC', repo: 'grpc/grpc-go', truthCount: 4, recall: 1, precision: 1 },
  { framework: 'Axum', repo: 'tokio-rs/axum', truthCount: 4, recall: 1, precision: 1 },
  { framework: 'Play', repo: 'playframework/play-samples', truthCount: 3, recall: 1, precision: 1 },
  { framework: 'Fiber', repo: 'gofiber/recipes', truthCount: 8, recall: 1, precision: 1 },
  { framework: 'Vapor', repo: 'vapor/api-template', truthCount: 4, recall: 1, precision: 1 },
  { framework: 'Chi', repo: 'go-chi/chi', truthCount: 12, recall: 1, precision: 1 },
  { framework: 'Echo', repo: 'xesina/golang-echo-realworld-example-app', truthCount: 20, recall: 1, precision: 1 },
  { framework: 'Phoenix', repo: 'gothinkster/elixir-phoenix-realworld-example-app', truthCount: 26, recall: 1, precision: 1 },
  { framework: 'net/http', repo: 'leeprovoost/go-rest-api-template', truthCount: 12, recall: 1, precision: 1 },
  { framework: 'Dream', repo: 'aantron/dream', truthCount: 2, recall: 1, precision: 1 },
  { framework: 'Cowboy', repo: 'ninenines/cowboy', truthCount: 1, recall: 1, precision: 1 },
  { framework: 'Compojure', repo: 'furkan3ayraktar/clojure-polylith-realworld-example-app', truthCount: 22, recall: 1, precision: 1 },
  { framework: 'Servant', repo: 'jeukshi/haskell-servant-realworld-example-app', truthCount: 19, recall: 1, precision: 1 },
  { framework: 'Plumber', repo: 'tmobile/r-tensorflow-api', truthCount: 1, recall: 1, precision: 1 },
] as const;

/** Frameworks with no synthetic fixtures — every framework now has at least one. */
export const NO_SYNTHETIC_FIXTURES: readonly string[] = [] as const;

// prettier-ignore
export const VENDORED_FIXTURE_ROWS: readonly VendoredFixtureRow[] = [
  { framework: 'FastAPI', name: 'fastapi-realworld@029eb77', structuralF1: 1, threshold: 0.92, groundTruth: 'openapi' },
  { framework: 'Hono', name: 'hono-open-api-starter@0d5f3bf', structuralF1: 1, threshold: 0.85, groundTruth: 'hand-labelled' },
  { framework: 'Symfony', name: 'symfony-realworld@5ad39de', structuralF1: 1, threshold: 0.85, groundTruth: 'openapi' },
] as const;
